const API_BASE = '/api/platform/v1';
const CSRF_BY_CONTEXT = new WeakMap();
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const CONTROL_CENTRE_ASYNC_FEEDBACK_STANDARD = 'ttoria-saas-async-feedback-v1';

export const CONTROL_CENTRE_ASYNC_ACTIONS = Object.freeze({
  logout: 'ttoria-control-centre.request.auth-logout.61c4491a',
  section: 'ttoria-control-centre.request.param-param.2688cb40',
  account: 'ttoria-control-centre.request.param-param.3f2b1665',
  createAccount: 'ttoria-control-centre.request.param-accounts.15eebed2',
  accountAction: 'ttoria-control-centre.request.param-accounts-param-actions.5836ecc0',
  acknowledgeSupport: 'ttoria-control-centre.request.param-accounts-param-support-cases-param-acknowledge.3703cd23',
  billingCheckout: 'ttoria-control-centre.request.param-accounts-param-billing-checkout.4be4c39b',
  billingPortal: 'ttoria-control-centre.request.param-accounts-param-billing-portal.e5049178',
  simulatePolicy: 'ttoria-control-centre.request.param-accounts-param-features-simulate.a1926847',
  savePolicy: 'ttoria-control-centre.request.param-accounts-param-features.8f906bcc',
  simulateAccountCapacity: 'ttoria-control-centre.request.param-accounts-param-capacity-simulate.8cc0c04a',
  saveAccountCapacity: 'ttoria-control-centre.request.param-accounts-param-capacity.80ae2f34',
  simulatePlanCapacity: 'ttoria-control-centre.request.param-plans-param-capacity-simulate.fc850fd9',
  savePlanCapacity: 'ttoria-control-centre.request.param-plans-param-capacity.e12687d5',
  retryOperation: 'ttoria-control-centre.request.param-operations-param-retry.2cd072ff',
  createIncident: 'ttoria-control-centre.request.param-incidents.e782963c',
  updateIncident: 'ttoria-control-centre.request.param-incidents-param.2feb84f4',
  resolveIncident: 'ttoria-control-centre.request.param-incidents-param-resolve.1b2ba85e',
  ownerSession: 'ttoria-control-centre.request.param-auth-session.57836d4c',
  registerPasskeyOptions: 'ttoria-control-centre.request.param-auth-passkeys-register-options.24f0d201',
  registerPasskeyVerify: 'ttoria-control-centre.request.param-auth-passkeys-register-verify.827ebb96',
  elevationOptions: 'ttoria-control-centre.request.param-auth-elevation-options.273928cf',
  elevationVerify: 'ttoria-control-centre.request.param-auth-elevation-verify.7fc15b5d',
  guideContent: 'ttoria-control-centre.request.param-guide-content.231619c8',
  guideRevision: 'ttoria-control-centre.request.param-guide-content-revisions-param.80024393',
  guideAction: 'ttoria-control-centre.request.param-guide-content-actions.e32c89e0',
  guideOperation: 'ttoria-control-centre.request.param-operations-param.e46ebebe',
});

export const SECTION_ENDPOINTS = Object.freeze({
  today: `${API_BASE}/today`,
  accounts: `${API_BASE}/accounts`,
  billing: `${API_BASE}/billing`,
  usage: `${API_BASE}/usage`,
  operations: `${API_BASE}/operations`,
  incidents: `${API_BASE}/incidents`,
  security: `${API_BASE}/security`,
  audit: `${API_BASE}/audit`,
  settings: `${API_BASE}/settings`,
});

function clean(value = '') {
  return String(value ?? '').trim();
}

export function isControlCentreAsyncFeedbackEnabled(ctx = {}) {
  const config = ctx?.sharedState?.asyncFeedback;
  return config?.enabled === true
    && clean(config?.standard) === CONTROL_CENTRE_ASYNC_FEEDBACK_STANDARD
    && clean(config?.appId) === 'ttoria-control-centre';
}

function controlCentreActivityErrorDetail(error, fallback = 'Please try again.') {
  return clean(error?.message || fallback || 'Please try again.').replace(/\s+/gu, ' ');
}

function beginControlCentreActivity(ctx = {}, config = {}, signal = null) {
  if (!isControlCentreAsyncFeedbackEnabled(ctx)) return null;
  if (!clean(config?.action)) {
    throw new Error('Control Centre async feedback is active without a catalogued action.');
  }
  const activity = ctx?.ui?.activity;
  if (!activity || typeof activity.begin !== 'function') {
    throw new Error('Control Centre async feedback is active without its governed activity scope.');
  }
  return activity.begin({
    action: clean(config.action),
    opClass: clean(config.opClass),
    summary: clean(config.summary),
    detail: clean(config.detail),
    silent: config.silent === true,
    ...(signal ? { signal } : {}),
  });
}

export async function runControlCentreActivity(ctx = {}, config = {}, signal = null, operation = async () => {}) {
  const handle = beginControlCentreActivity(ctx, config, signal);
  try {
    const result = await operation();
    handle?.succeed({
      summary: clean(config.successSummary || `${config.summary || 'Control Centre'} ready`),
      detail: clean(config.successDetail),
    });
    return result;
  } catch (error) {
    if (clean(error?.name) === 'AbortError') {
      handle?.cancel({ reason: 'request-aborted' });
    } else {
      handle?.fail({
        summary: clean(config.errorSummary || `${config.summary || 'Control Centre'} failed`),
        detail: controlCentreActivityErrorDetail(error, config.errorDetail),
      });
    }
    throw error;
  }
}

function requestActivity(action, opClass, summary, options = {}) {
  return {
    action,
    opClass,
    summary,
    ...options,
  };
}

function base64UrlToBytes(value = '') {
  const normalized = clean(value).replaceAll('-', '+').replaceAll('_', '/');
  const binary = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function bytesToBase64Url(value) {
  if (value == null) return null;
  const bytes = new Uint8Array(value);
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}

function registrationOptions(options = {}) {
  return {
    ...options,
    challenge: base64UrlToBytes(options.challenge),
    user: { ...options.user, id: base64UrlToBytes(options.user?.id) },
    excludeCredentials: (options.excludeCredentials || []).map((item) => ({
      ...item,
      id: base64UrlToBytes(item.id),
    })),
  };
}

function assertionOptions(options = {}) {
  return {
    ...options,
    challenge: base64UrlToBytes(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((item) => ({
      ...item,
      id: base64UrlToBytes(item.id),
    })),
  };
}

function serializeCredential(credential) {
  if (!credential?.response) throw new Error('The passkey response was not returned.');
  const response = credential.response;
  return {
    id: credential.id,
    rawId: bytesToBase64Url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment || undefined,
    clientExtensionResults: typeof credential.getClientExtensionResults === 'function'
      ? credential.getClientExtensionResults()
      : {},
    response: {
      clientDataJSON: bytesToBase64Url(response.clientDataJSON),
      ...(response.attestationObject ? {
        attestationObject: bytesToBase64Url(response.attestationObject),
        transports: typeof response.getTransports === 'function' ? response.getTransports() : [],
        publicKeyAlgorithm: typeof response.getPublicKeyAlgorithm === 'function'
          ? response.getPublicKeyAlgorithm()
          : undefined,
      } : {
        authenticatorData: bytesToBase64Url(response.authenticatorData),
        signature: bytesToBase64Url(response.signature),
        userHandle: response.userHandle ? bytesToBase64Url(response.userHandle) : null,
      }),
    },
  };
}

function credentialClient(ctx = {}) {
  const supportsPasskeys = (candidate) => Boolean(
    candidate
    && typeof candidate.create === 'function'
    && typeof candidate.get === 'function'
  );
  const contextualClient = supportsPasskeys(ctx?.webAuthnCredentials)
    ? ctx.webAuthnCredentials
    : supportsPasskeys(ctx?.credentials)
      ? ctx.credentials
      : null;
  // The platform shell uses ctx.credentials for the fetch setting
  // "same-origin". Only treat it as a WebAuthn client when it actually
  // implements the CredentialsContainer create/get contract.
  const browserClient = typeof navigator !== 'undefined' && supportsPasskeys(navigator.credentials)
    ? navigator.credentials
    : null;
  const client = contextualClient || browserClient;
  if (!client || typeof client.create !== 'function' || typeof client.get !== 'function') {
    const error = new Error('This browser cannot use passkeys. Use a supported browser or the configured authenticator-app fallback.');
    error.code = 'PASSKEY_BROWSER_UNAVAILABLE';
    throw error;
  }
  return client;
}

function createRequestError(response, payload = {}) {
  const errorPayload = payload?.error && typeof payload.error === 'object' ? payload.error : {};
  const error = new Error(
    clean(errorPayload.message)
    || clean(payload?.message)
    || `The request could not be completed (${Number(response?.status || 0) || 'unknown'}).`
  );
  error.code = clean(errorPayload.code) || (response?.status === 403 ? 'FORBIDDEN' : 'REQUEST_FAILED');
  error.status = Number(response?.status || 0);
  error.requestId = clean(payload?.requestId);
  error.details = errorPayload.details || null;
  return error;
}

function normalizeResponsePayload(payload) {
  if (payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'ok')) {
    if (payload.ok === false) {
      const error = new Error(clean(payload?.error?.message) || 'The request could not be completed.');
      error.code = clean(payload?.error?.code) || 'REQUEST_FAILED';
      error.requestId = clean(payload?.requestId);
      error.details = payload?.error?.details || null;
      throw error;
    }
    return payload.data;
  }
  return payload;
}

async function resolveDirectCsrfToken(ctx = {}, fetchImpl) {
  if (ctx && typeof ctx === 'object' && CSRF_BY_CONTEXT.has(ctx)) {
    return CSRF_BY_CONTEXT.get(ctx);
  }
  const response = await fetchImpl(`${API_BASE}/auth/session`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });
  const text = await response.text();
  let payload = {};
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = {};
    }
  }
  if (!response.ok) throw createRequestError(response, payload);
  const normalized = normalizeResponsePayload(payload);
  const token = clean(normalized?.csrfToken);
  if (!token) {
    const error = new Error('Your secure session could not be verified. Please sign in again.');
    error.code = 'CSRF_SESSION_MISSING';
    throw error;
  }
  if (ctx && typeof ctx === 'object') CSRF_BY_CONTEXT.set(ctx, token);
  return token;
}

async function requestControlCentreRaw(ctx = {}, path = '', {
  method = 'GET',
  body,
  signal,
  idempotencyKey = '',
  headers: extraHeaders = {},
} = {}) {
  const headers = new Headers(extraHeaders || {});
  headers.set('accept', 'application/json');
  if (body !== undefined) headers.set('content-type', 'application/json');
  if (clean(idempotencyKey)) {
    headers.set('idempotency-key', clean(idempotencyKey));
    headers.set('x-idempotency-key', clean(idempotencyKey));
  }
  const normalizedMethod = clean(method).toUpperCase() || 'GET';
  const init = {
    method: normalizedMethod,
    headers,
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  if (UNSAFE_METHODS.has(normalizedMethod) && !headers.has('x-csrf-token')) {
    const contextualToken = clean(
      ctx?.auth?.csrfToken
      || ctx?.sharedState?.csrfToken
      || ctx?.csrfToken
    );
    if (contextualToken) {
      headers.set('x-csrf-token', contextualToken);
    } else {
      const fetchImpl = typeof ctx?.fetch === 'function'
        ? ctx.fetch
        : (typeof window !== 'undefined' ? window.fetch.bind(window) : null);
      if (fetchImpl) {
        headers.set('x-csrf-token', await resolveDirectCsrfToken(ctx, fetchImpl));
      } else if (ctx?.apiClient && typeof ctx.apiClient.request === 'function') {
        const sessionPayload = normalizeResponsePayload(
          await ctx.apiClient.request(`${API_BASE}/auth/session`, {
            method: 'GET',
            headers: { accept: 'application/json' },
            credentials: 'same-origin',
          })
        );
        const token = clean(sessionPayload?.csrfToken);
        if (!token) {
          const error = new Error('Your secure session could not be verified. Please sign in again.');
          error.code = 'CSRF_SESSION_MISSING';
          throw error;
        }
        headers.set('x-csrf-token', token);
      }
    }
  }

  if (ctx?.apiClient && typeof ctx.apiClient.request === 'function') {
    return normalizeResponsePayload(await ctx.apiClient.request(path, init));
  }

  const fetchImpl = typeof ctx?.fetch === 'function'
    ? ctx.fetch
    : (typeof window !== 'undefined' ? window.fetch.bind(window) : null);
  if (!fetchImpl) throw new Error('The Control Centre connection is not available.');
  const response = await fetchImpl(path, init);
  const text = await response.text();
  let payload = {};
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = {};
    }
  }
  if (!response.ok) throw createRequestError(response, payload);
  return normalizeResponsePayload(payload);
}

export function requestControlCentre(ctx = {}, path = '', options = {}) {
  const { activity = null, ...requestOptions } = options || {};
  return runControlCentreActivity(
    ctx,
    activity || {},
    requestOptions.signal || null,
    () => requestControlCentreRaw(ctx, path, requestOptions)
  );
}

export function loadControlCentreSection(ctx = {}, sectionId = 'today', {
  signal,
  query = {},
  silent = false,
} = {}) {
  const endpoint = SECTION_ENDPOINTS[sectionId] || SECTION_ENDPOINTS.today;
  const url = new URL(endpoint, window.location.origin);
  for (const [key, value] of Object.entries(query || {})) {
    const normalized = clean(value);
    if (normalized) url.searchParams.set(key, normalized);
  }
  return requestControlCentre(ctx, `${url.pathname}${url.search}`, {
    signal,
    activity: requestActivity(
      CONTROL_CENTRE_ASYNC_ACTIONS.section,
      'foreground-read',
      silent ? `Refreshing ${sectionId}` : `Loading ${sectionId}`,
      {
        silent,
        successSummary: `${sectionId} updated`,
        errorSummary: `${sectionId} could not be updated`,
      }
    ),
  });
}

export function loadTutorAccount(ctx = {}, accountId = '', {
  signal,
  query = {},
  silent = false,
} = {}) {
  const endpoint = `${API_BASE}/accounts/${encodeURIComponent(clean(accountId))}`;
  const url = new URL(endpoint, window.location.origin);
  for (const [key, value] of Object.entries(query || {})) {
    const normalized = clean(value);
    if (normalized) url.searchParams.set(key, normalized);
  }
  return requestControlCentre(
    ctx,
    `${url.pathname}${url.search}`,
    {
      signal,
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.account,
        'foreground-read',
        silent ? 'Refreshing tutor account' : 'Opening tutor account',
        {
          silent,
          successSummary: 'Tutor account updated',
          errorSummary: 'Tutor account could not be opened',
        }
      ),
    }
  );
}

export function createTutorAccount(ctx = {}, payload = {}, idempotencyKey = '') {
  return requestControlCentre(ctx, `${API_BASE}/accounts`, {
    method: 'POST',
    body: payload,
    idempotencyKey,
    activity: requestActivity(
      CONTROL_CENTRE_ASYNC_ACTIONS.createAccount,
      'user-mutation',
      'Creating tutor account',
      {
        successSummary: 'Tutor account request accepted',
        errorSummary: 'Tutor account could not be created',
      }
    ),
  });
}

export function performTutorAccountAction(ctx = {}, accountId = '', action = '', payload = {}, idempotencyKey = '') {
  return requestControlCentre(
    ctx,
    `${API_BASE}/accounts/${encodeURIComponent(clean(accountId))}/actions`,
    {
      method: 'POST',
      body: {
        action: clean(action),
        ...payload,
      },
      idempotencyKey,
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.accountAction,
        'user-mutation',
        'Requesting tutor account change',
        {
          successSummary: 'Tutor account change accepted',
          errorSummary: 'Tutor account change failed',
        }
      ),
    }
  );
}

export function acknowledgeTutorSupportCase(
  ctx = {},
  accountId = '',
  supportCaseId = '',
  reason = '',
  idempotencyKey = ''
) {
  return requestControlCentre(
    ctx,
    `${API_BASE}/accounts/${encodeURIComponent(clean(accountId))}/support-cases/${encodeURIComponent(clean(supportCaseId))}/acknowledge`,
    {
      method: 'POST',
      body: { reason: clean(reason) },
      idempotencyKey,
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.acknowledgeSupport,
        'user-mutation',
        'Marking support problem received',
        {
          successSummary: 'Support problem marked received',
          errorSummary: 'Support problem could not be updated',
        }
      ),
    }
  );
}

export function createTutorAccountCheckout(ctx = {}, accountId = '', reason = '', idempotencyKey = '') {
  return requestControlCentre(
    ctx,
    `${API_BASE}/accounts/${encodeURIComponent(clean(accountId))}/billing/checkout`,
    {
      method: 'POST',
      body: { reason: clean(reason) },
      idempotencyKey,
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.billingCheckout,
        'user-mutation',
        'Opening secure checkout',
        {
          successSummary: 'Secure checkout ready',
          errorSummary: 'Secure checkout could not be opened',
        }
      ),
    }
  );
}

export function createTutorAccountBillingPortal(ctx = {}, accountId = '', reason = '', idempotencyKey = '') {
  return requestControlCentre(
    ctx,
    `${API_BASE}/accounts/${encodeURIComponent(clean(accountId))}/billing/portal`,
    {
      method: 'POST',
      body: { reason: clean(reason) },
      idempotencyKey,
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.billingPortal,
        'user-mutation',
        'Opening secure billing portal',
        {
          successSummary: 'Secure billing portal ready',
          errorSummary: 'Secure billing portal could not be opened',
        }
      ),
    }
  );
}

export function simulateTutorAccountPolicy(ctx = {}, accountId = '', payload = {}) {
  return requestControlCentre(
    ctx,
    `${API_BASE}/accounts/${encodeURIComponent(clean(accountId))}/features/simulate`,
    {
      method: 'POST',
      body: payload,
      idempotencyKey: payload.idempotencyKey || '',
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.simulatePolicy,
        'user-mutation',
        'Previewing feature changes',
        {
          successSummary: 'Feature impact preview ready',
          errorSummary: 'Feature preview failed',
        }
      ),
    }
  );
}

export function saveTutorAccountPolicy(ctx = {}, accountId = '', payload = {}, idempotencyKey = '') {
  return requestControlCentre(
    ctx,
    `${API_BASE}/accounts/${encodeURIComponent(clean(accountId))}/features`,
    {
      method: 'POST',
      body: payload,
      idempotencyKey,
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.savePolicy,
        'user-mutation',
        'Saving feature changes',
        {
          successSummary: 'Feature change request accepted',
          errorSummary: 'Feature changes could not be saved',
        }
      ),
    }
  );
}

export function simulateTutorAccountCapacity(ctx = {}, accountId = '', payload = {}) {
  return requestControlCentre(
    ctx,
    `${API_BASE}/accounts/${encodeURIComponent(clean(accountId))}/capacity/simulate`,
    {
      method: 'POST',
      body: payload,
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.simulateAccountCapacity,
        'user-mutation',
        'Previewing active student limit',
        {
          successSummary: 'Active student limit preview ready',
          errorSummary: 'Active student limit preview failed',
        }
      ),
    }
  );
}

export function saveTutorAccountCapacity(ctx = {}, accountId = '', payload = {}, idempotencyKey = '') {
  return requestControlCentre(
    ctx,
    `${API_BASE}/accounts/${encodeURIComponent(clean(accountId))}/capacity`,
    {
      method: 'POST',
      body: payload,
      idempotencyKey,
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.saveAccountCapacity,
        'user-mutation',
        'Saving active student limit',
        {
          successSummary: 'Active student limit request accepted',
          errorSummary: 'Active student limit could not be saved',
        }
      ),
    }
  );
}

export function simulatePlanCapacity(ctx = {}, planId = '', payload = {}) {
  return requestControlCentre(
    ctx,
    `${API_BASE}/plans/${encodeURIComponent(clean(planId))}/capacity/simulate`,
    {
      method: 'POST',
      body: payload,
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.simulatePlanCapacity,
        'user-mutation',
        'Previewing plan limit',
        {
          successSummary: 'Plan limit preview ready',
          errorSummary: 'Plan limit preview failed',
        }
      ),
    }
  );
}

export function savePlanCapacity(ctx = {}, planId = '', payload = {}, idempotencyKey = '') {
  return requestControlCentre(
    ctx,
    `${API_BASE}/plans/${encodeURIComponent(clean(planId))}/capacity`,
    {
      method: 'POST',
      body: payload,
      idempotencyKey,
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.savePlanCapacity,
        'user-mutation',
        'Saving plan limit',
        {
          successSummary: 'Plan limit request accepted',
          errorSummary: 'Plan limit could not be saved',
        }
      ),
    }
  );
}

export function retryOperation(ctx = {}, operationId = '', idempotencyKey = '', reason = '') {
  return requestControlCentre(
    ctx,
    `${API_BASE}/operations/${encodeURIComponent(clean(operationId))}/retry`,
    {
      method: 'POST',
      body: {
        ...(clean(reason) ? { reason: clean(reason) } : {}),
      },
      idempotencyKey,
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.retryOperation,
        'user-mutation',
        'Trying the change again',
        {
          successSummary: 'Change retry accepted',
          errorSummary: 'Change could not be retried',
        }
      ),
    }
  );
}

export function createIncident(ctx = {}, payload = {}, idempotencyKey = '') {
  return requestControlCentre(ctx, `${API_BASE}/incidents`, {
    method: 'POST',
    body: payload,
    idempotencyKey,
    activity: requestActivity(
      CONTROL_CENTRE_ASYNC_ACTIONS.createIncident,
      'user-mutation',
      'Recording service problem',
      {
        successSummary: 'Service problem recorded',
        errorSummary: 'Service problem could not be recorded',
      }
    ),
  });
}

export function updateIncident(
  ctx = {},
  incidentId = '',
  payload = {},
  idempotencyKey = ''
) {
  return requestControlCentre(
    ctx,
    `${API_BASE}/incidents/${encodeURIComponent(clean(incidentId))}`,
    {
      method: 'PATCH',
      body: payload,
      idempotencyKey,
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.updateIncident,
        'user-mutation',
        'Updating service problem',
        {
          successSummary: 'Service problem updated',
          errorSummary: 'Service problem could not be updated',
        }
      ),
    }
  );
}

export function resolveIncident(
  ctx = {},
  incidentId = '',
  payload = {},
  idempotencyKey = ''
) {
  return requestControlCentre(
    ctx,
    `${API_BASE}/incidents/${encodeURIComponent(clean(incidentId))}/resolve`,
    {
      method: 'POST',
      body: payload,
      idempotencyKey,
      activity: requestActivity(
        CONTROL_CENTRE_ASYNC_ACTIONS.resolveIncident,
        'user-mutation',
        'Resolving service problem',
        {
          successSummary: 'Service problem resolved',
          errorSummary: 'Service problem could not be resolved',
        }
      ),
    }
  );
}

export function loadOwnerSession(ctx = {}, { signal, silent = false } = {}) {
  return requestControlCentre(ctx, `${API_BASE}/auth/session`, {
    signal,
    activity: requestActivity(
      CONTROL_CENTRE_ASYNC_ACTIONS.ownerSession,
      'foreground-read',
      silent ? 'Refreshing secure session' : 'Checking secure session',
      {
        silent,
        successSummary: 'Secure session checked',
        errorSummary: 'Secure session could not be checked',
      }
    ),
  });
}

export async function registerOwnerPasskey(ctx = {}, deviceName = '') {
  const credentials = credentialClient(ctx);
  const start = await requestControlCentre(ctx, `${API_BASE}/auth/passkeys/register/options`, {
    method: 'POST',
    body: {},
    activity: requestActivity(
      CONTROL_CENTRE_ASYNC_ACTIONS.registerPasskeyOptions,
      'user-mutation',
      'Preparing backup passkey',
      {
        successSummary: 'Passkey prompt ready',
        errorSummary: 'Passkey setup could not start',
      }
    ),
  });
  const credential = await credentials.create({
    publicKey: registrationOptions(start?.options || {}),
  });
  return requestControlCentre(ctx, `${API_BASE}/auth/passkeys/register/verify`, {
    method: 'POST',
    body: {
      challengeId: clean(start?.challengeId),
      deviceName: clean(deviceName) || 'Backup owner passkey',
      response: serializeCredential(credential),
    },
    activity: requestActivity(
      CONTROL_CENTRE_ASYNC_ACTIONS.registerPasskeyVerify,
      'user-mutation',
      'Saving backup passkey',
      {
        successSummary: 'Backup passkey added',
        errorSummary: 'Backup passkey could not be saved',
      }
    ),
  });
}

export async function elevateOwnerSessionWithPasskey(ctx = {}, reason = '') {
  const credentials = credentialClient(ctx);
  const start = await requestControlCentre(ctx, `${API_BASE}/auth/elevation/options`, {
    method: 'POST',
    body: {},
    activity: requestActivity(
      CONTROL_CENTRE_ASYNC_ACTIONS.elevationOptions,
      'user-mutation',
      'Preparing identity confirmation',
      {
        successSummary: 'Passkey confirmation ready',
        errorSummary: 'Identity confirmation could not start',
      }
    ),
  });
  const credential = await credentials.get({
    publicKey: assertionOptions(start?.options || {}),
  });
  return requestControlCentre(ctx, `${API_BASE}/auth/elevation/verify`, {
    method: 'POST',
    body: {
      challengeId: clean(start?.challengeId),
      reason: clean(reason),
      response: serializeCredential(credential),
    },
    activity: requestActivity(
      CONTROL_CENTRE_ASYNC_ACTIONS.elevationVerify,
      'user-mutation',
      'Confirming sensitive changes',
      {
        successSummary: 'Sensitive changes confirmed',
        errorSummary: 'Identity could not be confirmed',
      }
    ),
  });
}

export function loadControlCentreGuideContent(ctx = {}, { signal } = {}) {
  return requestControlCentre(ctx, `${API_BASE}/guide/content`, {
    signal,
    activity: requestActivity(CONTROL_CENTRE_ASYNC_ACTIONS.guideContent, 'foreground-read', 'Loading Guide text', {
      successSummary: 'Guide text ready', errorSummary: 'Guide text could not be loaded',
    }),
  });
}

export function loadControlCentreGuideRevision(ctx = {}, revision, { signal } = {}) {
  return requestControlCentre(ctx, `${API_BASE}/guide/content/revisions/${encodeURIComponent(String(revision))}`, {
    signal,
    activity: requestActivity(CONTROL_CENTRE_ASYNC_ACTIONS.guideRevision, 'background-refresh', 'Loading earlier Guide text', {
      successSummary: 'Earlier Guide text ready', errorSummary: 'Earlier Guide text could not be loaded',
    }),
  });
}

export function applyControlCentreGuideContent(ctx = {}, payload, { etag, idempotencyKey, signal } = {}) {
  return requestControlCentre(ctx, `${API_BASE}/guide/content/actions`, {
    method: 'POST', body: payload, signal, idempotencyKey, headers: { 'if-match': etag },
    activity: requestActivity(CONTROL_CENTRE_ASYNC_ACTIONS.guideAction, 'user-mutation', 'Saving Guide change', {
      successSummary: 'Guide change request accepted', errorSummary: 'Guide change could not be confirmed',
    }),
  });
}

export function loadControlCentreGuideOperation(ctx = {}, operationId, { signal } = {}) {
  return requestControlCentre(ctx, `${API_BASE}/operations/${encodeURIComponent(clean(operationId))}`, {
    signal,
    activity: requestActivity(CONTROL_CENTRE_ASYNC_ACTIONS.guideOperation, 'foreground-read', 'Checking Guide change', {
      silent: true, successSummary: 'Guide change status updated', errorSummary: 'Guide change status is unavailable',
    }),
  });
}
